/**
 * Neue Aufgabe erstellen – eigenständige Tab-Seite
 * 2-Spalten-Layout: Formular links, Dokumentenvorschau rechts
 * KI-Autofill mit Handlungsempfehlung
 */
import { useState, useEffect, useCallback } from 'react';
import {
  CheckSquare, Check, Loader2, X, Sparkles, FileText, Eye,
  AlertTriangle, ArrowRight, Clock, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitEntityCreated, ENTITY_TYPES } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

/** Farbmarkierung für KI-befüllte Felder */
function fieldStyle(fieldKey, kiStatus) {
  if (!kiStatus) return '';
  if (kiStatus[fieldKey] === 'found') return 'ring-2 ring-orange-400 bg-orange-50';
  if (kiStatus[fieldKey] === 'not_found') return 'ring-2 ring-red-300 bg-red-50';
  return '';
}

/** Mini-Dokumentenvorschau */
function DokumentMiniVorschau({ url, name, dateityp }) {
  if (!url) return null;
  const isPdf = dateityp?.toLowerCase().endsWith('.pdf') || name?.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(name || '') || /\.(jpg|jpeg|png|gif|webp)$/i.test(dateityp || '');

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-xs font-medium text-slate-600 flex items-center gap-1.5 truncate">
          <Eye className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{name || 'Dokument'}</span>
        </p>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
          onClick={() => window.open(url, '_blank')}>
          <Eye className="w-3 h-3 mr-1" /> Groß
        </Button>
      </div>
      <div className="flex-1 overflow-hidden bg-slate-100 min-h-[300px]">
        {isPdf ? (
          <object data={url} type="application/pdf" className="w-full h-full border-0" style={{ minHeight: '100%' }}>
            <iframe src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
              className="w-full h-full border-0" title="Vorschau" />
          </object>
        ) : isImage ? (
          <div className="w-full h-full flex items-center justify-center p-2">
            <img src={url} alt="Vorschau" className="max-w-full max-h-full object-contain rounded" />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            <FileText className="w-8 h-8" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function NeueAufgabePage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};

  const [form, setForm] = useState({
    titel: params.titel || '',
    beschreibung: params.beschreibung || '',
    kategorie: params.kategorie || 'sonstiges',
    prioritaet: params.prioritaet || 'mittel',
    faelligkeit: params.faelligkeit || '',
    erinnerung: '',
    massnahme_id: params.massnahmeId || '',
  });
  const [saving, setSaving] = useState(false);
  const [kiLoading, setKiLoading] = useState(false);
  const [massnahmen, setMassnahmen] = useState([]);
  const [kiStatus, setKiStatus] = useState(null);
  const [kiStats, setKiStats] = useState(null);
  const [handlungsempfehlung, setHandlungsempfehlung] = useState(null);
  const [duplikatHinweis, setDuplikatHinweis] = useState(null);

  const hasDocument = !!params.dokumentVorschauUrl;
  const canUseKi = !!(params.ocrText?.length > 10 || params.dokumentId);

  // Maßnahmen für diesen Klienten laden
  useEffect(() => {
    if (params.klientId) {
      betreuungAPI.listMassnahmen({ person_id: params.klientId })
        .then(data => setMassnahmen(Array.isArray(data) ? data : []))
        .catch(() => setMassnahmen([]));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** KI-Autofill mit Handlungsempfehlung */
  const handleKiAutofill = async () => {
    if (!canUseKi) { toast.error('Kein Dokument vorhanden'); return; }

    setKiLoading(true);
    setKiStatus(null);
    setKiStats(null);
    setHandlungsempfehlung(null);
    setDuplikatHinweis(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/extract-aufgabe-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ocr_text: params.ocrText || null,
          dokument_id: params.dokumentId || null,
          klient_id: params.klientId || null,
          klient_name: params.klientName || null,
        }),
      });
      const data = await res.json();

      if (data.success && data.daten) {
        setForm(prev => {
          const next = { ...prev };
          if (data.daten.titel && data.felder_status.titel === 'found') next.titel = data.daten.titel;
          if (data.daten.beschreibung && data.felder_status.beschreibung === 'found') next.beschreibung = data.daten.beschreibung;
          if (data.daten.kategorie && data.felder_status.kategorie === 'found') next.kategorie = data.daten.kategorie;
          if (data.daten.prioritaet && data.felder_status.prioritaet === 'found') next.prioritaet = data.daten.prioritaet;
          if (data.daten.faelligkeit && data.felder_status.faelligkeit === 'found') next.faelligkeit = data.daten.faelligkeit;
          return next;
        });

        setKiStatus(data.felder_status);
        setKiStats({ gefunden: data.anzahl_gefunden, nicht_gefunden: data.anzahl_nicht_gefunden });
        if (data.handlungsempfehlung) setHandlungsempfehlung(data.handlungsempfehlung);
        if (data.duplikat_hinweis) setDuplikatHinweis(data.duplikat_hinweis);

        toast.success(`Zeo: ${data.anzahl_gefunden} Felder gefunden`);
      } else {
        toast.error(data.error || 'KI-Analyse fehlgeschlagen');
      }
    } catch (e) {
      console.error('KI-Autofill Fehler:', e);
      toast.error('Fehler bei der KI-Analyse');
    } finally {
      setKiLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (kiStatus?.[key]) setKiStatus(prev => ({ ...prev, [key]: undefined }));
  };

  const handleSave = async () => {
    if (!form.titel.trim()) { toast.error('Titel ist erforderlich'); return; }
    setSaving(true);
    try {
      // Maßnahmen-Bezeichnung ermitteln
      const selectedMassnahme = massnahmen.find(m => m.id === form.massnahme_id);
      const massnahmeBezeichnung = selectedMassnahme
        ? (selectedMassnahme.bezeichnung || selectedMassnahme.typ || '')
        : '';

      const result = await betreuungAPI.createAufgabe({
        klient_id: params.klientId || null,
        titel: form.titel,
        beschreibung: form.beschreibung || null,
        kategorie: form.kategorie,
        prioritaet: form.prioritaet,
        faellig_am: form.faelligkeit || null,
        erinnerung: form.erinnerung || null,
        dokument_id: params.dokumentId || null,
        massnahme_id: form.massnahme_id || null,
        massnahme_bezeichnung: massnahmeBezeichnung || null,
        betreuter_name: params.klientName || null,
        status: 'offen'
      });

      // Automatischen Verlaufseintrag erstellen (Dokument-Eingang)
      const aufgabeId = result?.id || result?.aufgabe_id;
      if (aufgabeId) {
        const heute = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const verlaufText = params.dokumentName
          ? `Posteingang: "${params.dokumentName}" am ${heute} eingegangen. Aufgabe aus Posteingang erstellt.`
          : `Aufgabe am ${heute} erstellt.`;

        await betreuungAPI.createAufgabenVerlauf(aufgabeId, {
          text: verlaufText,
          typ: 'dokument',
        }).catch(() => {}); // Silent - Verlauf ist optional
      }

      toast.success(`Aufgabe "${form.titel}" erstellt`);
      emitEntityCreated(ENTITY_TYPES.AUFGABE, result);
      closeTab(activeTab.id);
    } catch (e) {
      console.error('Fehler:', e);
      toast.error('Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = useCallback(() => {
    closeTab(activeTab.id);
  }, [closeTab, activeTab?.id]);

  return (
    <div className={`flex gap-4 ${hasDocument ? 'max-w-5xl' : 'max-w-xl'} mx-auto`} data-testid="neue-aufgabe-page">
      {/* Linke Spalte: Formular */}
      <div className={hasDocument ? 'flex-1 min-w-0' : 'w-full'}>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckSquare className="w-5 h-5 text-amber-600" />
                Neue Aufgabe erstellen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={handleClose} data-testid="close-neue-aufgabe">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>
              {params.klientName ? `Aufgabe für ${params.klientName}` : 'Neue Aufgabe erstellen'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* KI-Autofill Button */}
            {canUseKi && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-indigo-50 border border-indigo-100">
                <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
                <p className="text-xs text-indigo-700 flex-1">
                  Zeo analysiert das Dokument und schlägt eine konkrete Aufgabe mit Handlungsempfehlung vor.
                </p>
                <Button
                  size="sm" variant="outline"
                  onClick={handleKiAutofill}
                  disabled={kiLoading}
                  className="shrink-0 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  data-testid="ki-autofill-aufgabe-btn"
                >
                  {kiLoading ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analysiere...</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> KI-Autofill</>
                  )}
                </Button>
              </div>
            )}

            {/* Handlungsempfehlung */}
            {handlungsempfehlung && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2" data-testid="handlungsempfehlung">
                <div className="flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">Handlungsempfehlung</span>
                </div>
                <p className="text-xs text-emerald-700">{handlungsempfehlung.zusammenfassung}</p>
                {handlungsempfehlung.naechste_schritte?.length > 0 && (
                  <ol className="text-xs text-emerald-700 space-y-1 pl-4">
                    {handlungsempfehlung.naechste_schritte.map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <ArrowRight className="w-3 h-3 mt-0.5 shrink-0 text-emerald-500" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                )}
                {handlungsempfehlung.frist_erkannt && handlungsempfehlung.frist_details && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">
                    <Clock className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-medium">Frist: {handlungsempfehlung.frist_details}</span>
                  </div>
                )}
                {handlungsempfehlung.risiko_bei_nichthandeln && (
                  <div className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 rounded px-2 py-1 mt-1">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{handlungsempfehlung.risiko_bei_nichthandeln}</span>
                  </div>
                )}
              </div>
            )}

            {/* Duplikat-Hinweis */}
            {duplikatHinweis && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">{duplikatHinweis}</p>
              </div>
            )}

            {/* KI-Status Legende */}
            {kiStatus && kiStats && (
              <div className="flex items-center gap-4 text-xs px-1">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-orange-400" />
                  <span className="text-slate-600">Von KI gefunden ({kiStats.gefunden})</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-red-300" />
                  <span className="text-slate-600">Nicht gefunden ({kiStats.nicht_gefunden})</span>
                </span>
              </div>
            )}

            {/* Titel */}
            <div>
              <Label htmlFor="na-titel">Titel *</Label>
              <Input id="na-titel" value={form.titel}
                onChange={e => updateField('titel', e.target.value)}
                placeholder="Was ist zu tun?" required data-testid="aufgabe-titel-input"
                className={fieldStyle('titel', kiStatus)} />
            </div>

            {/* Beschreibung */}
            <div>
              <Label htmlFor="na-beschreibung">Beschreibung</Label>
              <Textarea id="na-beschreibung" value={form.beschreibung}
                onChange={e => updateField('beschreibung', e.target.value)}
                placeholder="Details zur Aufgabe..." rows={3}
                className={fieldStyle('beschreibung', kiStatus)} />
            </div>

            {/* Maßnahme (Bezug) */}
            {massnahmen.length > 0 && (
              <div>
                <Label>Bezug zur Maßnahme</Label>
                <Select value={form.massnahme_id || 'none'} onValueChange={v => updateField('massnahme_id', v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Maßnahme zuordnen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Keine --</SelectItem>
                    {massnahmen.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.bezeichnung || m.typ || 'Maßnahme'} {m.leistungsgeber_name ? `(${m.leistungsgeber_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Kategorie und Priorität */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kategorie</Label>
                <Select value={form.kategorie} onValueChange={v => updateField('kategorie', v)}>
                  <SelectTrigger className={fieldStyle('kategorie', kiStatus)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="posteingang">Posteingang</SelectItem>
                    <SelectItem value="wiedervorlage">Wiedervorlage</SelectItem>
                    <SelectItem value="frist">Frist</SelectItem>
                    <SelectItem value="termin">Termin</SelectItem>
                    <SelectItem value="pruefung">Prüfung</SelectItem>
                    <SelectItem value="antrag">Antrag stellen</SelectItem>
                    <SelectItem value="kommunikation">Kommunikation</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorität</Label>
                <Select value={form.prioritaet} onValueChange={v => updateField('prioritaet', v)}>
                  <SelectTrigger className={fieldStyle('prioritaet', kiStatus)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="niedrig">Niedrig</SelectItem>
                    <SelectItem value="mittel">Mittel</SelectItem>
                    <SelectItem value="hoch">Hoch</SelectItem>
                    <SelectItem value="dringend">Dringend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Fälligkeit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="na-faelligkeit">Fällig am</Label>
                <Input id="na-faelligkeit" type="date" value={form.faelligkeit}
                  onChange={e => updateField('faelligkeit', e.target.value)}
                  className={fieldStyle('faelligkeit', kiStatus)} />
              </div>
              <div>
                <Label htmlFor="na-erinnerung">Erinnerung</Label>
                <Input id="na-erinnerung" type="date" value={form.erinnerung}
                  onChange={e => setForm(p => ({ ...p, erinnerung: e.target.value }))} />
              </div>
            </div>

            {/* Aktionen */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose} data-testid="cancel-neue-aufgabe">Abbrechen</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-neue-aufgabe">
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...</>
                  : <><Check className="w-4 h-4 mr-2" /> Aufgabe erstellen</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rechte Spalte: Dokumentenvorschau */}
      {hasDocument && (
        <div className="w-80 shrink-0" data-testid="neue-aufgabe-vorschau">
          <DokumentMiniVorschau
            url={params.dokumentVorschauUrl}
            name={params.dokumentName}
            dateityp={params.dokumentDateityp}
          />
        </div>
      )}
    </div>
  );
}
